const WINDOW_ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const DEFAULT_LOCAL_API_ORIGIN = 'http://127.0.0.1:8000';
const PRODUCTION_API_ORIGIN = 'https://api.arabianacademy.org';

function isLoopbackHost(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase();
  return normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1';
}

function readHostname(value: string): string {
  try {
    return new URL(value).hostname;
  } catch {
    return '';
  }
}

function resolveApiOrigin(): string {
  const envApiOrigin = String(import.meta.env.VITE_API_URL || '').trim();
  const localOverride = String(import.meta.env.VITE_LOCAL_API_URL || DEFAULT_LOCAL_API_ORIGIN).trim();
  const allowRemoteFromLocal = String(import.meta.env.VITE_ALLOW_REMOTE_API_IN_LOCAL || '').toLowerCase() === 'true';
  const windowHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const envHost = envApiOrigin ? readHostname(envApiOrigin) : '';

  if (!envApiOrigin && (windowHost === 'www.arabianacademy.org' || windowHost === 'arabianacademy.org')) {
    return PRODUCTION_API_ORIGIN;
  }

  if (
    WINDOW_ORIGIN
    && isLoopbackHost(windowHost)
    && envApiOrigin
    && envHost
    && !isLoopbackHost(envHost)
    && !allowRemoteFromLocal
  ) {
    return localOverride;
  }

  return envApiOrigin || WINDOW_ORIGIN;
}

const rawApiOrigin = resolveApiOrigin();

export const API_ORIGIN = rawApiOrigin.replace(/\/+$/, '');

const BACKEND_PATH_PREFIXES = [
  '/api',
  '/auth',
  '/student',
  '/employee',
  '/reception',
  '/hr',
  '/manager',
  '/diagnostics',
  '/marketing',
  '/sales',
  '/summer-training/api',
  '/accountant',
  '/instructor',
  '/realtime',
  '/uploads',
  '/job-applications',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/logout',
];

function hasPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isBackendPath(pathname: string): boolean {
  return BACKEND_PATH_PREFIXES.some((prefix) => hasPrefix(pathname, prefix));
}

export function toApiUrl(pathOrUrl: string | URL): string {
  const raw = typeof pathOrUrl === 'string' ? pathOrUrl : pathOrUrl.toString();
  if (!raw) return raw;

  if (/^(data|blob|mailto|tel):/i.test(raw)) {
    return raw;
  }

  const base = WINDOW_ORIGIN || API_ORIGIN || 'http://localhost';
  let parsed: URL;
  try {
    parsed = new URL(raw, base);
  } catch {
    return raw;
  }

  if (!isBackendPath(parsed.pathname)) {
    return raw;
  }

  const isRelative = raw.startsWith('/');
  const sameWindowOrigin = Boolean(WINDOW_ORIGIN) && parsed.origin === WINDOW_ORIGIN;
  const sameApiOrigin = Boolean(API_ORIGIN) && parsed.origin === API_ORIGIN;
  if (!isRelative && !sameWindowOrigin && !sameApiOrigin) {
    return parsed.toString();
  }

  if (!API_ORIGIN) {
    return parsed.toString();
  }

  const api = new URL(API_ORIGIN);
  parsed.protocol = api.protocol;
  parsed.host = api.host;
  return parsed.toString();
}

export function toApiWsUrl(pathOrUrl: string): string {
  const raw = (pathOrUrl || '').trim();
  if (!raw) return raw;
  if (/^wss?:\/\//i.test(raw)) {
    return raw;
  }

  const normalized = toApiUrl(raw);
  try {
    const base = WINDOW_ORIGIN || API_ORIGIN || 'http://localhost';
    const parsed = new URL(normalized, base);
    if (parsed.protocol === 'https:') parsed.protocol = 'wss:';
    else if (parsed.protocol === 'http:') parsed.protocol = 'ws:';
    return parsed.toString();
  } catch {
    return raw;
  }
}

let fetchShimInstalled = false;

function resolveInputUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installApiFetchShim(): void {
  if (fetchShimInstalled || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  fetchShimInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const currentUrl = resolveInputUrl(input);
    const rewrittenUrl = toApiUrl(currentUrl);

    if (rewrittenUrl === currentUrl) {
      return nativeFetch(input, init);
    }

    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    if (init?.headers) {
      const initHeaders = new Headers(init.headers);
      initHeaders.forEach((value, key) => headers.set(key, value));
    }
    if (!headers.has('X-Requested-With')) {
      headers.set('X-Requested-With', 'XMLHttpRequest');
    }

    const finalInit: RequestInit = {
      ...init,
      headers,
      credentials: 'include',
    };

    if (input instanceof Request) {
      const rewrittenRequest = new Request(rewrittenUrl, input);
      const mode = init?.mode ?? input.mode;
      if (mode === 'same-origin') {
        finalInit.mode = 'cors';
      }
      return nativeFetch(rewrittenRequest, finalInit);
    }

    return nativeFetch(rewrittenUrl, finalInit);
  }) as typeof window.fetch;
}
